import React, { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Text } from '../text'
import { VStack } from '../vstack'

export type InfoBannerTone = 'neutral' | 'info' | 'warning' | 'critical'

export type InfoBannerProps = {
    title: string
    message?: string
    tone?: InfoBannerTone
    children?: ReactNode
    className?: string
}

const toneSurface: Record<InfoBannerTone, string> = {
    neutral: 'bg-background-elevation',
    info: 'bg-status-info',
    warning: 'bg-status-warning',
    critical: 'bg-status-critical',
}

const toneTitle: Record<InfoBannerTone, string> = {
    neutral: 'text-content-default',
    info: 'text-content-inverted',
    warning: 'text-content-inverted',
    critical: 'text-content-inverted',
}

/**
 * Universal info banner (replaces the legacy `aos-ui` `InfoBanner`/`InfoBar`):
 * a centred tonal block with a title, optional muted message, and optional
 * action children. `tone` picks the surface + title ink from `status/*` tokens;
 * `neutral` sits on `bg/elevation` with `content/default` text. The action slot
 * spans the banner width so `w-full` children stretch as intended.
 */
export const InfoBanner: React.FC<InfoBannerProps> = ({
    title,
    message,
    tone = 'neutral',
    children,
    className,
}) => (
    <VStack className={twMerge('items-center px-10 py-[30px]', toneSurface[tone], className)}>
        <Text size='body' weight='medium' className={twMerge('text-center', toneTitle[tone])}>
            {title}
        </Text>
        {message && (
            <Text size='caption' tone='muted' className='pt-2 text-center'>
                {message}
            </Text>
        )}
        {children && <Box className='w-full pt-4'>{children}</Box>}
    </VStack>
)

InfoBanner.displayName = 'InfoBanner'
